import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import AlertCircleOutlineIcon from 'mdi-react/AlertCircleOutlineIcon';
import AutoFixIcon from 'mdi-react/AutoFixIcon';
import EraserIcon from 'mdi-react/EraserIcon';
import FitToPageOutlineIcon from 'mdi-react/FitToPageOutlineIcon';
import HandBackRightOutlineIcon from 'mdi-react/HandBackRightOutlineIcon';
import MagnifyMinusOutlineIcon from 'mdi-react/MagnifyMinusOutlineIcon';
import MagnifyPlusOutlineIcon from 'mdi-react/MagnifyPlusOutlineIcon';
import RotateLeftIcon from 'mdi-react/RotateLeftIcon';
import RotateRightIcon from 'mdi-react/RotateRightIcon';
import CursorDefaultOutlineIcon from 'mdi-react/CursorDefaultOutlineIcon';
import RedoVariantIcon from 'mdi-react/RedoVariantIcon';
import SelectAllIcon from 'mdi-react/SelectAllIcon';
import UndoVariantIcon from 'mdi-react/UndoVariantIcon';
import type { FloorPlanSketch, SketchPoint } from '../models/floorplan-sketch';
import { SKETCH_EXTENT } from '../models/floorplan-sketch';
import { editorTheme, reviewPalette } from '../utils/editor-theme';
import {
  REVIEW_KINDS,
  countsOf,
  deskSizeOf,
  detectedByKind,
  detectedCountOf,
  duplicatesOf,
  inDrawingOrder,
  isSymbolKind,
  isUncertain,
  itemsInBox,
  markedItem,
  movedItems,
  resizedItem,
  reviewItemsOf,
  rotatedItems,
  scaledItems,
  similarTo,
  sketchOfReview,
  withDetected,
  withKindFor,
  withoutDetected,
  withoutDetectedOf,
  withoutItems,
  type ReviewItem,
  type ReviewKind,
} from '../utils/sketch-review';
import {
  historyOf, recorded, redone, undone, type History,
} from '../utils/review-history';
import ImageReviewSelection from './image-review-selection.component';
import ConfirmDialog from './confirm-dialog.component';

interface Props {
  /** What the tracer found. */
  readonly sketch: FloorPlanSketch;
  /** The uploaded image, shown under what was found. Empty when there is nothing to show. */
  readonly imageUrl: string;
  /** Height over width of the picture the sketch's coordinates are normalised to. */
  readonly aspect: number;
  readonly saving: boolean;
  readonly onBack: () => void;
  /** The corrected sketch, to make the plan from. */
  readonly onCreate: (sketch: FloorPlanSketch) => void;
  /** Degrees clockwise the picture was turned before it was read. */
  readonly turn?: number;
  /** Reads the picture again, turned `turn` degrees clockwise in all. */
  readonly onTurn?: (turn: number) => void;
  /** The picture is being read again. */
  readonly reading?: boolean;
  /** What the reading is doing, while it runs. */
  readonly readingLabel?: string;
}

const KIND_COLOURS: Record<ReviewKind, string> = reviewPalette;

type Mode = 'select' | 'pan' | ReviewKind;

/** Which part of the picture is in view: its top-left corner in the picture's own units, and how far in. */
interface View {
  readonly left: number;
  readonly top: number;
  readonly zoom: number;
}

/** What a drag is doing: marking something missed, selecting by box, moving the selection, or moving the view. */
type Drag =
  | { readonly type: 'mark'; readonly from: SketchPoint; readonly to: SketchPoint }
  | { readonly type: 'box'; readonly from: SketchPoint; readonly to: SketchPoint; readonly adding: boolean }
  | { readonly type: 'move'; readonly from: SketchPoint; readonly to: SketchPoint }
  | { readonly type: 'pan'; readonly fromX: number; readonly fromY: number; readonly view: View };

const FIT: View = { left: 0, top: 0, zoom: 1 };
const ZOOM = { min: 1, max: 8, step: 1.25 };

/** The view kept inside the picture: a zoomed view never shows beyond its edges. */
const clampedView = (view: View, height: number): View => {
  const zoom = Math.min(ZOOM.max, Math.max(ZOOM.min, view.zoom));
  const [width, tall] = [SKETCH_EXTENT / zoom, height / zoom];
  return {
    zoom,
    left: Math.min(SKETCH_EXTENT - width, Math.max(0, view.left)),
    top: Math.min(height - tall, Math.max(0, view.top)),
  };
};

/** The view zoomed by `factor`, keeping the point `at` (picture units) where it is on screen. */
const zoomedView = (view: View, factor: number, at: [number, number], height: number): View => {
  const zoom = Math.min(ZOOM.max, Math.max(ZOOM.min, view.zoom * factor));
  const keep = view.zoom / zoom;
  return clampedView({
    zoom,
    left: at[0] - ((at[0] - view.left) * keep),
    top: at[1] - ((at[1] - view.top) * keep),
  }, height);
};

const cursorOf = (mode: Mode, drag: Drag | null): string => {
  if (drag?.type === 'pan') return 'grabbing';
  if (mode === 'pan') return 'grab';
  return mode === 'select' ? 'default' : 'crosshair';
};

/** A turn written back as 0 to 359 degrees. */
const wholeTurn = (degrees: number): number => (((Math.round(degrees * 10) / 10) % 360) + 360) % 360;

/** How far an arrow key moves the selection, in sketch units; ten times as far with Shift. */
const NUDGE = 1;

const pointsOf = (outline: readonly SketchPoint[], aspect: number): string => outline
  .map((point) => `${point.x},${point.y * aspect}`)
  .join(' ');

/** How far an arrow key moves a zoomed picture with nothing selected: a share of what is in view. */
const PAN_STEP = 0.1;

/** How wide the overview of a zoomed picture is drawn, in pixels. */
const OVERVIEW_WIDTH = 160;

const isAddingTo = (event: React.PointerEvent | React.MouseEvent): boolean => event.shiftKey || event.ctrlKey || event.metaKey;

/**
 * The step between reading an image and making a plan of it.
 *
 * The tracer is right about most of a clean drawing and wrong about some of any drawing: a
 * cupboard read as a desk, a desk it could not close off, writing it found but cannot read.
 * Correcting that here, over the picture it came from, is quicker than finding it later in an
 * empty editor with nothing to compare against — and a desk found here becomes a bookable desk
 * with its "[" shape, where one fixed later has to be drawn from scratch.
 *
 * Works like a drawing tool: click to select, Shift- or Ctrl-click to add to the selection, drag
 * round finds to select them all, drag a selection to move it, and undo anything. What was
 * found can be cleared all at once, to mark a badly read picture by hand, and put back again.
 * Zoomed in, the picture is moved with the hand tool, Space or the middle button, the wheel, the
 * arrow keys with nothing selected, or by dragging the frame on the overview.
 */
const ImageReview = ({
  sketch, imageUrl, aspect, saving, onBack, onCreate, turn = 0, onTurn, reading = false, readingLabel,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();
  // What the tracer found, as it found it: what clearing takes away and restoring puts back.
  const [detected] = useState<ReviewItem[]>(() => reviewItemsOf(sketch, aspect));
  const [history, setHistory] = useState<History<ReviewItem[]>>(() => historyOf(detected));
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [mode, setMode] = useState<Mode>('select');
  const [view, setView] = useState<View>(FIT);
  // The view as it is now, for the wheel listener, which has to decide at once whether it scrolls the page.
  const viewRef = useRef(view);
  viewRef.current = view;
  // Held down, Space pans whatever tool is chosen, as in any drawing tool.
  const spaceHeld = useRef(false);
  const [turnText, setTurnText] = useState(String(turn));
  // A turn waiting for the administrator to agree to lose their corrections.
  const [pendingTurn, setPendingTurn] = useState<number | null>(null);
  const [hidden, setHidden] = useState<ReadonlySet<ReviewKind>>(new Set());
  const [drag, setDrag] = useState<Drag | null>(null);
  // The overview's frame being dragged, to move the view with it.
  const [steering, setSteering] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  // The canvas and the overview over it: the wheel works on either.
  const canvasRef = useRef<HTMLDivElement>(null);
  const overviewRef = useRef<SVGSVGElement>(null);
  // The last text edit, so typing a name is one step to undo rather than one per letter.
  const typing = useRef<string | null>(null);
  // A find focused by a click is selected by the click; only Tab selects on focus.
  const pointing = useRef(false);

  // A new selection starts a new step for typing, even back on a find typed in before.
  useEffect(() => {
    typing.current = null;
  }, [selectedIds]);

  // Ctrl or ⌘ with the wheel — and a trackpad pinch, which arrives as one — zooms round the
  // pointer; the wheel alone moves a zoomed picture. Listened to directly, because React's wheel
  // listener cannot stop the page scrolling instead.
  useEffect(() => {
    const svg = svgRef.current;
    const canvas = canvasRef.current;
    if (!svg || !canvas) return undefined;

    const tall = SKETCH_EXTENT * aspect;

    const onWheel = (event: WheelEvent): void => {
      const bounds = svg.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;

      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        setView((current) => {
          const at: [number, number] = [
            current.left + (((event.clientX - bounds.left) / bounds.width) * (SKETCH_EXTENT / current.zoom)),
            current.top + (((event.clientY - bounds.top) / bounds.height) * (tall / current.zoom)),
          ];
          return zoomedView(current, event.deltaY < 0 ? ZOOM.step : 1 / ZOOM.step, at, tall);
        });
        return;
      }

      // Not zoomed in, the wheel scrolls the page as usual.
      if (viewRef.current.zoom <= ZOOM.min) return;
      event.preventDefault();
      setView((current) => {
        if (current.zoom <= ZOOM.min) return current;
        return clampedView({
          zoom: current.zoom,
          left: current.left + ((event.deltaX / bounds.width) * (SKETCH_EXTENT / current.zoom)),
          top: current.top + ((event.deltaY / bounds.height) * (tall / current.zoom)),
        }, tall);
      });
    };

    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [aspect]);

  const items = history.present;
  const deskSize = useMemo(() => deskSizeOf(sketch), [sketch]);
  const counts = countsOf(items);
  // Kept the same between renders that change neither, so the overview is not drawn again for nothing.
  const visible = useMemo(() => inDrawingOrder(items.filter((item) => !hidden.has(item.kind))), [items, hidden]);
  // An undo can take away what was selected; what is not there any more is not selected.
  const selected = items.filter((item) => selectedIds.has(item.id));
  const selection = new Set(selected.map((item) => item.id));
  const uncertain = visible.filter(isUncertain);
  const unnamedLabels = items.filter((item) => item.kind === 'label' && !item.text.trim()).length;
  const height = SKETCH_EXTENT * aspect;
  const markingKind: ReviewKind | null = mode === 'select' || mode === 'pan' ? null : mode;
  const viewWidth = SKETCH_EXTENT / view.zoom;
  const viewHeight = height / view.zoom;
  const detectedLeft = detectedCountOf(items, detected);
  const detectedKinds = detectedByKind(items, detected);

  const zoomBy = (factor: number, at?: [number, number]): void => {
    setView((current) => zoomedView(current, factor, at ?? [
      current.left + (SKETCH_EXTENT / current.zoom / 2), current.top + (height / current.zoom / 2),
    ], height));
  };

  /** Reads the picture again at another turn — asking first when that throws corrections away. */
  const requestTurn = (next: number): void => {
    if (!onTurn) return;
    const target = wholeTurn(next);
    if (target === wholeTurn(turn)) return;
    if (history.past.length) {
      setPendingTurn(target);
      return;
    }
    onTurn(target);
  };

  const commit = (next: ReviewItem[]): void => {
    typing.current = null;
    setHistory((current) => recorded(current, next));
  };

  const commitText = (id: string, text: string): void => {
    const next = items.map((item) => (item.id === id ? { ...item, text } : item));
    if (typing.current === id) {
      setHistory((current) => ({ ...current, present: next }));
      return;
    }
    typing.current = id;
    setHistory((current) => recorded(current, next));
  };

  const undo = (): void => {
    typing.current = null;
    setHistory(undone);
  };

  const redo = (): void => {
    typing.current = null;
    setHistory(redone);
  };

  const removeSelected = (): void => {
    if (!selection.size) return;
    commit(withoutItems(items, selection));
    setSelectedIds(new Set());
  };

  const clearDetected = (): void => {
    if (!detectedLeft) return;
    commit(withoutDetected(items, detected));
    setSelectedIds(new Set());
  };

  const restoreDetected = (): void => {
    if (detectedLeft === detected.length) return;
    commit(withDetected(items, detected));
  };

  /** Every desk the tracer found, say, taken away at once; what was marked by hand stays. */
  const clearDetectedOf = (kind: ReviewKind): void => {
    commit(withoutDetectedOf(items, detected, kind));
    setSelectedIds(new Set());
  };

  const restoreDetectedOf = (kind: ReviewKind): void => {
    commit(withDetected(items, detected, kind));
  };

  /** The view moved to have the overview's point under the pointer in its middle. */
  const steerTo = (event: React.PointerEvent): void => {
    const bounds = overviewRef.current?.getBoundingClientRect();
    if (!bounds || !bounds.width || !bounds.height) return;
    const x = ((event.clientX - bounds.left) / bounds.width) * SKETCH_EXTENT;
    const y = ((event.clientY - bounds.top) / bounds.height) * height;
    setView((current) => clampedView({
      zoom: current.zoom,
      left: x - (SKETCH_EXTENT / current.zoom / 2),
      top: y - (height / current.zoom / 2),
    }, height));
  };

  const duplicateSelected = (): void => {
    if (!selection.size) return;
    const copies = duplicatesOf(items, selection);
    commit([...items, ...copies]);
    setSelectedIds(new Set(copies.map((copy) => copy.id)));
  };

  /** Where the pointer is, in sketch coordinates. */
  const pointAt = (event: React.PointerEvent): SketchPoint => {
    const bounds = svgRef.current?.getBoundingClientRect();

    if (!bounds || !bounds.width || !bounds.height) {
      return { x: 0, y: 0 };
    }

    // Through the view: the picture may be zoomed in and moved.
    const x = view.left + (((event.clientX - bounds.left) / bounds.width) * viewWidth);
    const y = (view.top + (((event.clientY - bounds.top) / bounds.height) * viewHeight)) / aspect;

    return {
      x: Math.min(SKETCH_EXTENT, Math.max(0, x)),
      y: Math.min(SKETCH_EXTENT, Math.max(0, y)),
    };
  };

  // Captured, so a drag past the edge of the picture still ends where the pointer is let go.
  const capture = (event: React.PointerEvent): void => {
    svgRef.current?.setPointerCapture?.(event.pointerId);
  };

  const finishDrag = (): void => {
    const ended = drag;
    setDrag(null);

    if (!ended) return;

    if (ended.type === 'mark' && markingKind) {
      const marked = markedItem(markingKind, ended.from, ended.to, aspect, deskSize);
      if (marked) {
        commit([...items, marked]);
        setSelectedIds(new Set([marked.id]));
      }
      return;
    }

    if (ended.type === 'box') {
      const inside = itemsInBox(visible, ended.from, ended.to);
      setSelectedIds(new Set([...(ended.adding ? Array.from(selection) : []), ...inside]));
      return;
    }

    if (ended.type === 'move') {
      const [dx, dy] = [ended.to.x - ended.from.x, ended.to.y - ended.from.y];
      if (Math.abs(dx) > 0.3 || Math.abs(dy) > 0.3) {
        commit(movedItems(items, selection, dx, dy));
      }
    }
  };

  /** What is drawn: the finds, with the selection where a drag is taking it. */
  const shown = drag?.type === 'move'
    ? movedItems(visible, selection, drag.to.x - drag.from.x, drag.to.y - drag.from.y)
    : visible;

  const onKeyDown = (event: React.KeyboardEvent): void => {
    const target = event.target as HTMLElement;
    // Keys something has already handled — a menu's arrows — and keys pressed outside the review
    // itself are not shortcuts. A menu opened from the review is placed elsewhere in the page,
    // and React still hands its keys to the review.
    if (event.defaultPrevented || !event.currentTarget.contains(target)) return;
    // Typing in a field is typing, not a shortcut.
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

    const command = event.ctrlKey || event.metaKey;
    const key = event.key.toLowerCase();
    const nudges: Record<string, [number, number]> = {
      ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1 / aspect], ArrowDown: [0, 1 / aspect],
    };

    if (!command && (event.key === '+' || event.key === '=')) {
      event.preventDefault();
      zoomBy(ZOOM.step);
    } else if (!command && event.key === '-') {
      event.preventDefault();
      zoomBy(1 / ZOOM.step);
    } else if (!command && event.key === '0') {
      event.preventDefault();
      setView(FIT);
    } else if (event.key === ' ' && (event.target as Element) === svgRef.current) {
      event.preventDefault();
      spaceHeld.current = true;
    } else if (command && key === 'z') {
      event.preventDefault();
      if (event.shiftKey) redo();
      else undo();
    } else if (command && key === 'y') {
      event.preventDefault();
      redo();
    } else if (command && key === 'a') {
      event.preventDefault();
      setSelectedIds(new Set(visible.map((item) => item.id)));
    } else if (command && key === 'd') {
      event.preventDefault();
      duplicateSelected();
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      removeSelected();
    } else if (event.key === 'Escape') {
      setSelectedIds(new Set());
      setMode('select');
      setDrag(null);
    } else if (nudges[event.key] && selection.size) {
      event.preventDefault();
      const step = event.shiftKey ? NUDGE * 10 : NUDGE;
      const [dx, dy] = nudges[event.key];
      commit(movedItems(items, selection, dx * step, dy * step));
    } else if (nudges[event.key] && view.zoom > ZOOM.min && svgRef.current?.contains(target)) {
      // Nothing selected to move: on the picture, the arrows move a zoomed picture instead.
      event.preventDefault();
      const step = (event.shiftKey ? PAN_STEP * 5 : PAN_STEP);
      const [dx, dy] = nudges[event.key];
      setView((current) => clampedView({
        zoom: current.zoom,
        left: current.left + (Math.sign(dx) * step * (SKETCH_EXTENT / current.zoom)),
        top: current.top + (Math.sign(dy) * step * (height / current.zoom)),
      }, height));
    }
  };

  // Where the picture sits: the tracer may have turned it square on a larger frame.
  const { frame } = sketch;
  const imageBox = frame && frame.width > 0
    ? {
      width: (frame.sourceWidth / frame.width) * SKETCH_EXTENT,
      height: (frame.sourceHeight / frame.width) * SKETCH_EXTENT,
      degrees: frame.degrees,
    }
    : { width: SKETCH_EXTENT, height, degrees: 0 };

  const picture = imageUrl && (
    <image
      href={imageUrl}
      x={(SKETCH_EXTENT - imageBox.width) / 2}
      y={(height - imageBox.height) / 2}
      width={imageBox.width}
      height={imageBox.height}
      opacity={0.55}
      transform={`rotate(${imageBox.degrees} ${SKETCH_EXTENT / 2} ${height / 2})`}
      preserveAspectRatio="none"
    />
  );

  // Drawn once for the overview while the view is dragged about: only the finds change it.
  const overviewFinds = useMemo(() => visible.map((item) => (
    <polygon
      key={item.id}
      points={pointsOf(item.outline, aspect)}
      fill={KIND_COLOURS[item.kind]}
      fillOpacity={item.kind === 'room' ? 0.08 : 0.4}
      stroke="none"
      pointerEvents="none"
    />
  )), [visible, aspect]);

  const dragBox = drag && (drag.type === 'mark' || drag.type === 'box') ? drag : null;
  const dragColour = dragBox?.type === 'mark' && markingKind ? KIND_COLOURS[markingKind] : editorTheme.accent;

  const selectKind = (kind: ReviewKind): void => {
    setSelectedIds(new Set(items.filter((item) => item.kind === kind).map((item) => item.id)));
  };

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      sx={{ p: 2, alignItems: 'flex-start' }}
      onKeyDown={onKeyDown}
      onKeyUp={(event): void => { if (event.key === ' ') spaceHeld.current = false; }}
    >
      <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h6" sx={{ color: editorTheme.ink }}>{t('floorplan-image-review-title')}</Typography>
        <Typography variant="body2" sx={{ color: editorTheme.inkDim }}>{t('floorplan-image-review-hint')}</Typography>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Tooltip title={String(t('floorplan-image-review-undo'))}>
            <span>
              <IconButton size="small" onClick={undo} disabled={!history.past.length} aria-label={String(t('floorplan-image-review-undo'))}>
                <UndoVariantIcon size={18} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={String(t('floorplan-image-review-redo'))}>
            <span>
              <IconButton
                size="small"
                onClick={redo}
                disabled={!history.future.length}
                aria-label={String(t('floorplan-image-review-redo'))}
              >
                <RedoVariantIcon size={18} />
              </IconButton>
            </span>
          </Tooltip>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={mode}
            onChange={(_, value: Mode | null): void => { if (value) setMode(value); }}
            sx={{ flexWrap: 'wrap' }}
          >
            <ToggleButton value="select" sx={{ textTransform: 'none' }} aria-label={String(t('floorplan-image-review-select'))}>
              <CursorDefaultOutlineIcon size={16} />
              <Box component="span" sx={{ ml: 0.5 }}>{t('floorplan-image-review-select')}</Box>
            </ToggleButton>
            <ToggleButton value="pan" sx={{ textTransform: 'none' }} aria-label={String(t('floorplan-image-review-pan'))}>
              <HandBackRightOutlineIcon size={16} />
              <Box component="span" sx={{ ml: 0.5 }}>{t('floorplan-image-review-pan')}</Box>
            </ToggleButton>
            {/* A fitting missed is marked as furniture and then said to be one: too many to offer each here. */}
            {REVIEW_KINDS.filter((kind) => !isSymbolKind(kind)).map((kind) => (
              <ToggleButton key={kind} value={kind} sx={{ textTransform: 'none', color: KIND_COLOURS[kind] }}>
                {t('floorplan-image-review-mark', { kind: String(t(`floorplan-image-review-kind-${kind}`)) })}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Tooltip title={String(t('floorplan-image-review-zoom-out'))}>
            <span>
              <IconButton
                size="small"
                onClick={(): void => zoomBy(1 / ZOOM.step)}
                disabled={view.zoom <= ZOOM.min}
                aria-label={String(t('floorplan-image-review-zoom-out'))}
              >
                <MagnifyMinusOutlineIcon size={18} />
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            variant="caption"
            data-testid="image-review-zoom"
            sx={{ minWidth: 40, textAlign: 'center', fontFamily: editorTheme.numeric }}
          >
            {`${Math.round(view.zoom * 100)}%`}
          </Typography>
          <Tooltip title={String(t('floorplan-image-review-zoom-in'))}>
            <span>
              <IconButton
                size="small"
                onClick={(): void => zoomBy(ZOOM.step)}
                disabled={view.zoom >= ZOOM.max}
                aria-label={String(t('floorplan-image-review-zoom-in'))}
              >
                <MagnifyPlusOutlineIcon size={18} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={String(t('floorplan-image-review-fit'))}>
            <IconButton size="small" onClick={(): void => setView(FIT)} aria-label={String(t('floorplan-image-review-fit'))}>
              <FitToPageOutlineIcon size={18} />
            </IconButton>
          </Tooltip>

          {onTurn && (
            <>
              <Box sx={{ width: 12 }} />
              <Tooltip title={String(t('floorplan-image-review-picture-left'))}>
                <span>
                  <IconButton
                    size="small"
                    onClick={(): void => requestTurn(turn - 90)}
                    disabled={reading || saving}
                    aria-label={String(t('floorplan-image-review-picture-left'))}
                  >
                    <RotateLeftIcon size={18} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={String(t('floorplan-image-review-picture-right'))}>
                <span>
                  <IconButton
                    size="small"
                    onClick={(): void => requestTurn(turn + 90)}
                    disabled={reading || saving}
                    aria-label={String(t('floorplan-image-review-picture-right'))}
                  >
                    <RotateRightIcon size={18} />
                  </IconButton>
                </span>
              </Tooltip>
              <TextField
                size="small"
                type="number"
                label={String(t('floorplan-image-review-picture-angle'))}
                value={turnText}
                disabled={reading || saving}
                inputProps={{ step: 0.5 }}
                onChange={(event): void => setTurnText(event.target.value)}
                onBlur={(): void => {
                  const parsed = Number(turnText);
                  if (Number.isFinite(parsed)) requestTurn(parsed);
                  else setTurnText(String(turn));
                }}
                onKeyDown={(event): void => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    const parsed = Number(turnText);
                    if (Number.isFinite(parsed)) requestTurn(parsed);
                  }
                }}
                sx={{ width: 120 }}
              />
              {reading && <CircularProgress size={16} aria-label={readingLabel} />}
              {reading && readingLabel && <Typography variant="caption" sx={{ color: editorTheme.inkDim }}>{readingLabel}</Typography>}
            </>
          )}
        </Stack>

        <Box
          sx={{
            width: '100%',
            maxWidth: `calc(70vh / ${aspect})`,
            border: `1px solid ${editorTheme.line}`,
            borderRadius: `${editorTheme.radius}px`,
            overflow: 'hidden',
            background: '#fff',
            position: 'relative',
          }}
          ref={canvasRef}
        >
          <svg
            ref={svgRef}
            data-testid="image-review-canvas"
            viewBox={`${view.left} ${view.top} ${viewWidth} ${viewHeight}`}
            // Focusable, so the shortcuts work after a click on the picture as well as on a find.
            tabIndex={0}
            aria-label={String(t('floorplan-image-review-canvas'))}
            preserveAspectRatio="none"
            style={{
              display: 'block',
              width: '100%',
              aspectRatio: `1 / ${aspect}`,
              cursor: cursorOf(mode, drag),
              touchAction: 'none',
              outline: 'none',
            }}
            onPointerDown={(event): void => {
              capture(event);
              svgRef.current?.focus?.({ preventScroll: true });

              // The hand tool, Space held down, or the middle button: move the view instead.
              if (mode === 'pan' || spaceHeld.current || event.button === 1) {
                event.preventDefault();
                setDrag({
                  type: 'pan', fromX: event.clientX, fromY: event.clientY, view,
                });
                return;
              }

              const at = pointAt(event);
              if (mode === 'select') {
                setDrag({
                  type: 'box', from: at, to: at, adding: isAddingTo(event),
                });
                return;
              }
              setDrag({ type: 'mark', from: at, to: at });
            }}
            onPointerMove={(event): void => {
              if (!drag) return;

              if (drag.type === 'pan') {
                const bounds = svgRef.current?.getBoundingClientRect();
                if (!bounds || !bounds.width || !bounds.height) return;
                setView(clampedView({
                  zoom: drag.view.zoom,
                  left: drag.view.left - (((event.clientX - drag.fromX) / bounds.width) * (SKETCH_EXTENT / drag.view.zoom)),
                  top: drag.view.top - (((event.clientY - drag.fromY) / bounds.height) * (height / drag.view.zoom)),
                }, height));
                return;
              }

              setDrag({ ...drag, to: pointAt(event) });
            }}
            onPointerUp={(event): void => {
              if (svgRef.current?.hasPointerCapture?.(event.pointerId)) {
                svgRef.current.releasePointerCapture(event.pointerId);
              }
              pointing.current = false;
              finishDrag();
            }}
            onPointerCancel={(): void => setDrag(null)}
          >
            {picture}

            {sketch.border.length >= 3 && (
              <polygon
                points={pointsOf(sketch.border, aspect)}
                fill="none"
                stroke={editorTheme.ink}
                strokeWidth={2}
                strokeDasharray="8 6"
                vectorEffect="non-scaling-stroke"
                pointerEvents="none"
              />
            )}

            {shown.map((item) => {
              const colour = KIND_COLOURS[item.kind];
              const isSelected = selection.has(item.id);
              return (
                <polygon
                  key={item.id}
                  data-testid={`review-item-${item.kind}`}
                  points={pointsOf(item.outline, aspect)}
                  fill={colour}
                  fillOpacity={item.kind === 'room' ? 0.08 : 0.3}
                  stroke={isSelected ? editorTheme.accent : colour}
                  strokeWidth={isSelected ? 4 : 1.5}
                  // A find the tracer is unsure of is dashed: the ones to look at first.
                  strokeDasharray={isUncertain(item) ? '6 4' : undefined}
                  style={{ cursor: mode === 'select' ? 'move' : cursorOf(mode, drag), outline: 'none' }}
                  // Outlines stay the same width however far the picture is zoomed.
                  vectorEffect="non-scaling-stroke"
                  tabIndex={0}
                  role="button"
                  aria-label={String(t(`floorplan-image-review-kind-${item.kind}`))}
                  aria-pressed={isSelected}
                  onFocus={(): void => {
                    if (pointing.current) {
                      pointing.current = false;
                      return;
                    }
                    if (!selection.has(item.id)) setSelectedIds(new Set([item.id]));
                  }}
                  onKeyDown={(event): void => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSelectedIds(new Set([item.id]));
                    }
                  }}
                  onPointerDown={(event): void => {
                    if (mode !== 'select' || spaceHeld.current || event.button === 1) return;
                    event.stopPropagation();
                    pointing.current = true;
                    capture(event);

                    if (isAddingTo(event)) {
                      const next = new Set(selection);
                      if (next.has(item.id)) next.delete(item.id);
                      else next.add(item.id);
                      setSelectedIds(next);
                      return;
                    }

                    if (!selection.has(item.id)) setSelectedIds(new Set([item.id]));
                    const at = pointAt(event);
                    setDrag({ type: 'move', from: at, to: at });
                  }}
                />
              );
            })}

            {dragBox && (
              <rect
                x={Math.min(dragBox.from.x, dragBox.to.x)}
                y={Math.min(dragBox.from.y, dragBox.to.y) * aspect}
                width={Math.abs(dragBox.to.x - dragBox.from.x)}
                height={Math.abs(dragBox.to.y - dragBox.from.y) * aspect}
                fill={dragColour}
                fillOpacity={0.12}
                stroke={dragColour}
                strokeDasharray="6 4"
                vectorEffect="non-scaling-stroke"
                pointerEvents="none"
              />
            )}
          </svg>

          {/* Zoomed in, the whole picture in a corner, with a frame round what is in view to drag about. */}
          {view.zoom > ZOOM.min && (
            <svg
              ref={overviewRef}
              data-testid="image-review-overview"
              role="img"
              aria-label={String(t('floorplan-image-review-overview'))}
              viewBox={`0 0 ${SKETCH_EXTENT} ${height}`}
              preserveAspectRatio="none"
              style={{
                position: 'absolute',
                right: 8,
                bottom: 8,
                // A corner of a small canvas, not the most of it.
                width: `min(${OVERVIEW_WIDTH}px, 30%)`,
                aspectRatio: `1 / ${aspect}`,
                background: '#fff',
                border: `1px solid ${editorTheme.line}`,
                borderRadius: editorTheme.radius,
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
                cursor: steering ? 'grabbing' : 'pointer',
                touchAction: 'none',
              }}
              onPointerDown={(event): void => {
                event.stopPropagation();
                overviewRef.current?.setPointerCapture?.(event.pointerId);
                setSteering(true);
                steerTo(event);
              }}
              onPointerMove={(event): void => { if (steering) steerTo(event); }}
              onPointerUp={(event): void => {
                if (overviewRef.current?.hasPointerCapture?.(event.pointerId)) {
                  overviewRef.current.releasePointerCapture(event.pointerId);
                }
                setSteering(false);
              }}
              onPointerCancel={(): void => setSteering(false)}
            >
              {picture}
              {overviewFinds}
              <rect
                data-testid="image-review-overview-frame"
                x={view.left}
                y={view.top}
                width={viewWidth}
                height={viewHeight}
                fill={editorTheme.accent}
                fillOpacity={0.12}
                stroke={editorTheme.accent}
                strokeWidth={2}
                vectorEffect="non-scaling-stroke"
                pointerEvents="none"
              />
            </svg>
          )}
        </Box>
        <Typography variant="caption" sx={{ color: editorTheme.inkFaint }}>{t('floorplan-image-review-shortcuts')}</Typography>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          width: { xs: '100%', md: 340 },
          flexShrink: 0,
          border: `1px solid ${editorTheme.line}`,
          borderRadius: `${editorTheme.radius}px`,
        }}
      >
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle2">{t('floorplan-image-review-found')}</Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {REVIEW_KINDS.filter((kind) => !isSymbolKind(kind) || counts[kind] > 0).map((kind) => {
                const name = String(t(`floorplan-image-review-kind-${kind}`));
                return (
                  <Stack key={kind} direction="row" alignItems="center" spacing={0.25}>
                    <Chip
                      size="small"
                      label={`${name} ${counts[kind]}`}
                      variant={hidden.has(kind) ? 'outlined' : 'filled'}
                      onClick={(): void => setHidden((current) => {
                        const next = new Set(current);
                        if (next.has(kind)) next.delete(kind);
                        else next.add(kind);
                        return next;
                      })}
                      sx={{
                        borderColor: KIND_COLOURS[kind],
                        color: hidden.has(kind) ? KIND_COLOURS[kind] : '#fff',
                        backgroundColor: hidden.has(kind) ? 'transparent' : KIND_COLOURS[kind],
                      }}
                    />
                    {/* A button of its own rather than the chip's delete icon: a chip also deletes on Delete. */}
                    {counts[kind] > 0 && (
                      <Tooltip title={String(t('floorplan-image-review-select-kind-of', { kind: name }))}>
                        <IconButton
                          size="small"
                          onClick={(): void => selectKind(kind)}
                          aria-label={String(t('floorplan-image-review-select-kind-of', { kind: name }))}
                          sx={{ p: 0.25, color: KIND_COLOURS[kind] }}
                        >
                          <SelectAllIcon size={14} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                );
              })}
            </Stack>
            <Typography variant="caption" sx={{ color: editorTheme.inkFaint }}>{t('floorplan-image-review-toggle-hint')}</Typography>
          </Stack>

          <Stack spacing={0.5}>
            <Typography variant="subtitle2">{t('floorplan-image-review-detected')}</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<EraserIcon size={16} />}
                onClick={clearDetected}
                disabled={!detectedLeft || saving}
                sx={{ textTransform: 'none' }}
              >
                {t('floorplan-image-review-clear-detected')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<AutoFixIcon size={16} />}
                onClick={restoreDetected}
                disabled={detectedLeft === detected.length || saving}
                sx={{ textTransform: 'none' }}
              >
                {t('floorplan-image-review-restore-detected')}
              </Button>
            </Stack>
            {/* One kind at a time: all the desks, all the rooms, all the doors — for a picture read
                well but for one kind of thing, where clearing everything would throw the rest away. */}
            {detectedKinds.length > 0 && (
              <Stack spacing={0.25} data-testid="image-review-detected-kinds">
                {detectedKinds.map(({ kind, left, missing }) => {
                  const name = String(t(`floorplan-image-review-kind-${kind}`));
                  return (
                    <Stack key={kind} direction="row" alignItems="center" spacing={0.5}>
                      <Box
                        sx={{
                          width: 10, height: 10, borderRadius: '50%', flexShrink: 0, backgroundColor: KIND_COLOURS[kind],
                        }}
                      />
                      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                        {t('floorplan-image-review-detected-kind-count', { kind: name, count: left, total: left + missing })}
                      </Typography>
                      <Button
                        size="small"
                        color="inherit"
                        onClick={(): void => clearDetectedOf(kind)}
                        disabled={!left || saving}
                        aria-label={String(t('floorplan-image-review-clear-kind', { kind: name }))}
                        sx={{ textTransform: 'none', minWidth: 0, px: 0.75 }}
                      >
                        {t('floorplan-image-review-clear-kind-short')}
                      </Button>
                      <Button
                        size="small"
                        color="inherit"
                        onClick={(): void => restoreDetectedOf(kind)}
                        disabled={!missing || saving}
                        aria-label={String(t('floorplan-image-review-restore-kind', { kind: name }))}
                        sx={{ textTransform: 'none', minWidth: 0, px: 0.75 }}
                      >
                        {t('floorplan-image-review-restore-kind-short')}
                      </Button>
                    </Stack>
                  );
                })}
              </Stack>
            )}
            <Typography variant="caption" data-testid="image-review-detected-count" sx={{ color: editorTheme.inkFaint }}>
              {t('floorplan-image-review-detected-count', { count: detectedLeft, total: detected.length })}
            </Typography>
            <Typography variant="caption" sx={{ color: editorTheme.inkDim }}>{t('floorplan-image-review-as-drawn-hint')}</Typography>
          </Stack>

          {uncertain.length > 0 && (
            <Alert
              severity="warning"
              icon={<AlertCircleOutlineIcon size={18} />}
              sx={{ py: 0 }}
              action={(
                <Button
                  size="small"
                  color="inherit"
                  onClick={(): void => setSelectedIds(new Set(uncertain.map((item) => item.id)))}
                  sx={{ textTransform: 'none' }}
                >
                  {t('floorplan-image-review-select-uncertain')}
                </Button>
              )}
            >
              {t('floorplan-image-review-uncertain', { count: uncertain.length })}
            </Alert>
          )}

          <ImageReviewSelection
            key={selected.map((item) => item.id).join(',')}
            selected={selected}
            aspect={aspect}
            metresAcross={sketch.metresAcrossImage ?? null}
            onKind={(kind): void => commit(withKindFor(items, selection, kind))}
            onText={(text): void => { if (selected.length === 1) commitText(selected[0].id, text); }}
            onRotate={(degrees): void => commit(rotatedItems(items, selection, degrees, aspect))}
            onScale={(factor): void => commit(scaledItems(items, selection, factor, aspect))}
            onResize={(length, breadth): void => commit(items.map((item) => (
              selection.has(item.id) ? resizedItem(item, length, breadth, aspect) : item
            )))}
            onDuplicate={duplicateSelected}
            onSelectSimilar={(): void => {
              if (selected.length === 1) setSelectedIds(new Set(similarTo(visible, selected[0].id, aspect)));
            }}
            onRemove={removeSelected}
          />

          {unnamedLabels > 0 && (
            <Alert severity="info" sx={{ py: 0 }}>{t('floorplan-image-review-empty-labels', { count: unnamedLabels })}</Alert>
          )}

          <Stack direction="row" spacing={1} alignItems="center">
            <Button onClick={onBack} disabled={saving} sx={{ textTransform: 'none' }}>{t('floorplan-image-review-back')}</Button>
            <Button
              variant="contained"
              disabled={saving}
              onClick={(): void => onCreate(sketchOfReview(sketch, items, aspect))}
              sx={{ textTransform: 'none' }}
            >
              {t('floorplan-image-review-create')}
            </Button>
            {saving && <CircularProgress size={18} />}
          </Stack>
        </Stack>
      </Paper>
      <ConfirmDialog
        open={pendingTurn !== null}
        messageKey="floorplan-image-review-turn-confirm"
        onConfirm={(): void => {
          if (pendingTurn !== null) onTurn?.(pendingTurn);
          setPendingTurn(null);
        }}
        onCancel={(): void => {
          setPendingTurn(null);
          setTurnText(String(turn));
        }}
      />
    </Stack>
  );
};

export default ImageReview;
